import { Fragment, type ReactNode } from "react";

/**
 * The small part of Markdown the legal texts use: headings, paragraphs, numbered and bulleted
 * lists, tables and **bold**. Rendered as React elements, never as HTML, so a text can not inject
 * anything into the till.
 */
export type Block =
  | { kind: "heading"; level: 1 | 2 | 3; text: string }
  | { kind: "paragraph"; text: string }
  | { kind: "list"; ordered: boolean; start: number; items: string[] }
  | { kind: "table"; header: string[]; rows: string[][] };

export function parseMarkdown(source: string): Block[] {
  const blocks: Block[] = [];
  const lines = source.replace(/\r\n/g, "\n").split("\n");
  let index = 0;

  while (index < lines.length) {
    const line = lines[index]!;
    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    if (heading) {
      blocks.push({ kind: "heading", level: heading[1]!.length as 1 | 2 | 3, text: heading[2]!.trim() });
      index++;
    } else if (/^\s*(\d+\.|[-*])\s+/.test(line)) {
      const ordered = /^\s*\d+\./.test(line);
      const start = ordered ? Number(/\d+/.exec(line)![0]) : 1;
      const items: string[] = [];
      while (index < lines.length && /^\s*(\d+\.|[-*])\s+/.test(lines[index]!)) {
        items.push(lines[index]!.replace(/^\s*(\d+\.|[-*])\s+/, "").trim());
        index++;
      }
      blocks.push({ kind: "list", ordered, start, items });
    } else if (line.trimStart().startsWith("|")) {
      const rows: string[][] = [];
      while (index < lines.length && lines[index]!.trimStart().startsWith("|")) {
        const cells = lines[index]!.trim()
          .replace(/^\||\|$/g, "")
          .split("|")
          .map((cell) => cell.trim());
        if (!cells.every((cell) => /^:?-+:?$/.test(cell))) rows.push(cells); // skip the |---| separator
        index++;
      }
      blocks.push({ kind: "table", header: rows[0] ?? [], rows: rows.slice(1) });
    } else if (line.trim() === "") {
      index++;
    } else {
      const text: string[] = [];
      while (index < lines.length && isParagraphLine(lines[index]!)) {
        text.push(lines[index]!.trim());
        index++;
      }
      blocks.push({ kind: "paragraph", text: text.join(" ") });
    }
  }
  return blocks;
}

function isParagraphLine(line: string): boolean {
  return line.trim() !== "" && !/^(#{1,3}\s|\s*(\d+\.|[-*])\s|\s*\|)/.test(line);
}

/** **bold** → <strong>; everything else stays plain text. */
export function renderInline(text: string): ReactNode {
  return text
    .split(/(\*\*[^*]+\*\*)/g)
    .map((part, index) =>
      part.startsWith("**") && part.endsWith("**") && part.length > 4 ? (
        <strong key={index}>{part.slice(2, -2)}</strong>
      ) : (
        <Fragment key={index}>{part}</Fragment>
      ),
    );
}

export function Markdown({ source }: { source: string }) {
  return (
    <>
      {parseMarkdown(source).map((block, index) => {
        switch (block.kind) {
          case "heading": {
            const Heading = block.level === 1 ? "h1" : block.level === 2 ? "h2" : "h3";
            return <Heading key={index}>{renderInline(block.text)}</Heading>;
          }
          case "paragraph":
            return <p key={index}>{renderInline(block.text)}</p>;
          case "list": {
            const List = block.ordered ? "ol" : "ul";
            return (
              <List key={index} start={block.ordered && block.start !== 1 ? block.start : undefined}>
                {block.items.map((item, itemIndex) => (
                  <li key={itemIndex}>{renderInline(item)}</li>
                ))}
              </List>
            );
          }
          case "table":
            return (
              <table key={index}>
                <thead>
                  <tr>
                    {block.header.map((cell, cellIndex) => (
                      <th key={cellIndex}>{renderInline(cell)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {block.rows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {row.map((cell, cellIndex) => (
                        <td key={cellIndex}>{renderInline(cell)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            );
        }
      })}
    </>
  );
}
